import { WebsiteBlue } from "@similarweb/icons/illustrations";
import { PromoBanner } from "@similarweb/ui-components";

import { showSegmentsMeteringHook } from "app/pages/segments/metering/SegmentsMetering";

import { ComposerWidthNotice } from "./styledComponents";
import { translateWithFallback as t } from "./translateWithFallback";

type NoSegmentBudgetNoticeProps = { hasSegmentsModule: boolean };

/**
 * Shown before the run starts: the builder still works, but with no segments to spend it can
 * only add whole domains, so a generalist site can never be narrowed down to the market. Pinned
 * to the composer's own width (720px) since it otherwise sits directly on the page canvas.
 */
export const NoSegmentBudgetNotice = ({ hasSegmentsModule }: NoSegmentBudgetNoticeProps) => (
    <ComposerWidthNotice data-automation="market-builder-v2-no-segment-budget-notice">
        <PromoBanner>
            <WebsiteBlue size={80} />
            <PromoBanner.Content>
                <PromoBanner.Title>
                    {hasSegmentsModule
                        ? t("market.builder.v2.segmentBudget.none.left.title", "You've used all your segments")
                        : t("market.builder.v2.segmentBudget.noModule.title", "Segments aren't included in your plan")}
                </PromoBanner.Title>
                <PromoBanner.Description>
                    {hasSegmentsModule
                        ? t(
                              "market.builder.v2.segmentBudget.none.left",
                              "You have used every segment your plan allows. The agent will still find whole websites, but it cannot carve the matching section out of a large general site — more segments unlock that.",
                          )
                        : t(
                              "market.builder.v2.segmentBudget.noModule",
                              "The agent can still identify relevant websites but isolating a specific section of a larger site (like a subdomain or subfolder) requires the Segments module.",
                          )}
                </PromoBanner.Description>
            </PromoBanner.Content>
            <PromoBanner.Cta onClick={showSegmentsMeteringHook}>
                {t("market.builder.v2.segmentBudget.upgrade", "See segment plans")}
            </PromoBanner.Cta>
        </PromoBanner>
    </ComposerWidthNotice>
);

type SegmentsOverBudgetNoticeProps = { segmentsOverBudget: number; maxSegments: number };

/**
 * Shown during the run, once classification is done: the agent found more sites worth
 * segmenting than the plan covers, and added the rest to the industry as whole domains. No
 * width override here — `PromoBanner`'s own `width: 100%` already matches the running
 * composer's width, since both sit in equal-width columns.
 */
// The fallbacks are interpolated here rather than left as placeholders: `translate` hands
// back the key itself when a catalog entry is missing, so an uninterpolated fallback is what
// real users would see.
const overBudgetTitle = (maxSegments: number): string => {
    if (maxSegments === 0) {
        return t("market.builder.v2.segmentBudget.overBudget.none.title", "No segments left in your plan");
    }
    if (maxSegments < 0) {
        return t(
            "market.builder.v2.segmentBudget.overBudget.perIndustry.title",
            "This industry reached its segment limit",
        );
    }
    return t("market.builder.v2.segmentBudget.overBudget.some.title", "Some websites didn't get their own segment");
};

const overBudgetDescription = (segmentsOverBudget: number, maxSegments: number): string => {
    if (maxSegments === 0) {
        return t(
            "market.builder.v2.segmentBudget.overBudget.none",
            `${segmentsOverBudget} websites deserved their own segment, but your plan has none left — they were added as whole websites instead.`,
            { count: segmentsOverBudget },
        );
    }

    if (maxSegments < 0) {
        // No account cap applies, so the overflow is this builder's own per-industry ceiling
        // — nothing the user's plan can change.
        return t(
            "market.builder.v2.segmentBudget.overBudget.perIndustry",
            `${segmentsOverBudget} more websites deserved their own segment than one industry can hold — they were added as whole websites instead.`,
            { count: segmentsOverBudget },
        );
    }

    return t(
        "market.builder.v2.segmentBudget.overBudget.some",
        `${segmentsOverBudget} more websites deserved their own segment, but your plan covers ${maxSegments} — they were added as whole websites instead.`,
        { count: segmentsOverBudget, allowed: maxSegments },
    );
};

export const SegmentsOverBudgetNotice = ({ segmentsOverBudget, maxSegments }: SegmentsOverBudgetNoticeProps) => (
    <div data-automation="market-builder-v2-segments-over-budget-notice">
        <PromoBanner>
            <WebsiteBlue size={80} />
            <PromoBanner.Content>
                <PromoBanner.Title>{overBudgetTitle(maxSegments)}</PromoBanner.Title>
                <PromoBanner.Description>
                    {overBudgetDescription(segmentsOverBudget, maxSegments)}
                </PromoBanner.Description>
            </PromoBanner.Content>
            {maxSegments >= 0 && (
                <PromoBanner.Cta onClick={showSegmentsMeteringHook}>
                    {t("market.builder.v2.segmentBudget.upgrade", "See segment plans")}
                </PromoBanner.Cta>
            )}
        </PromoBanner>
    </div>
);
